import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { DuoCheckIn, DuoCheckInResponse } from '@/types/dinoDuos';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { MessageSquare } from 'lucide-react';

interface DuoCheckInsCardProps {
  duoId: string;
}

export function DuoCheckInsCard({ duoId }: DuoCheckInsCardProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [checkIns, setCheckIns] = useState<DuoCheckIn[]>([]);
  const [responses, setResponses] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState<string | null>(null);

  useEffect(() => {
    if (!duoId) return;

    fetchCheckIns();
    setupRealtimeSubscription();
  }, [duoId]);

  const fetchCheckIns = async () => {
    try {
      const { data: checkInsData, error: checkInsError } = await supabase
        .from('duo_check_ins')
        .select('*')
        .eq('duo_id', duoId)
        .order('milestone_percent', { ascending: true });

      if (checkInsError) throw checkInsError;

      // Fetch responses
      const { data: responsesData, error: responsesError } = await supabase
        .from('duo_check_in_responses')
        .select(`
          *,
          user:profiles(id, username, name, image)
        `)
        .in('check_in_id', checkInsData?.map(ci => ci.id) || []);

      if (responsesError) throw responsesError;

      // Group responses by check-in
      const responsesByCheckIn = responsesData?.reduce((acc: any, response: any) => {
        if (!acc[response.check_in_id]) acc[response.check_in_id] = [];
        acc[response.check_in_id].push({
          ...response,
          user: Array.isArray(response.user) ? response.user[0] : response.user
        });
        return acc;
      }, {});

      setCheckIns(checkInsData?.map((ci: any) => ({
        ...ci,
        responses: responsesByCheckIn?.[ci.id] || []
      })) || []);
    } catch (error: any) {
      console.error('Error fetching check-ins:', error);
    }
  };

  const setupRealtimeSubscription = () => {
    const channel = supabase
      .channel(`duo-check-ins-${duoId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duo_check_ins',
          filter: `duo_id=eq.${duoId}`
        },
        () => {
          fetchCheckIns();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'duo_check_in_responses'
        },
        () => {
          fetchCheckIns();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSubmitResponse = async (checkInId: string) => {
    if (!user || !responses[checkInId]?.trim()) return;

    setSubmitting(checkInId);
    try {
      const { error } = await supabase
        .from('duo_check_in_responses')
        .insert({
          check_in_id: checkInId,
          user_id: user.id,
          response_text: responses[checkInId].trim()
        });

      if (error) throw error;

      setResponses(prev => ({ ...prev, [checkInId]: '' }));
      toast({
        title: "Response added!",
        description: "Your thoughts have been shared with the group."
      });
    } catch (error: any) {
      console.error('Error submitting response:', error);
      toast({
        title: "Failed to submit response",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setSubmitting(null);
    }
  };

  const hasUserResponded = (checkIn: DuoCheckIn) => {
    return checkIn.responses?.some(r => r.user_id === user?.id);
  };

  if (checkIns.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5" />
          Check-In Prompts
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {checkIns.map((checkIn) => {
          const userResponse = checkIn.responses?.find(r => r.user_id === user?.id);
          
          return (
            <div key={checkIn.id} className="space-y-3 pb-6 border-b last:border-b-0 last:pb-0">
              <div className="flex items-start gap-3">
                <Badge variant="secondary" className="mt-1">
                  {checkIn.milestone_percent}% Mark
                </Badge>
                <p className="flex-1 font-medium">{checkIn.prompt_text}</p>
              </div>

              {!hasUserResponded(checkIn) && (
                <div className="space-y-2">
                  <Textarea
                    placeholder="Share your thoughts..."
                    value={responses[checkIn.id] || ''}
                    onChange={(e) => setResponses(prev => ({
                      ...prev,
                      [checkIn.id]: e.target.value
                    }))}
                    className="min-h-[80px]"
                  />
                  <Button
                    onClick={() => handleSubmitResponse(checkIn.id)}
                    disabled={!responses[checkIn.id]?.trim() || submitting === checkIn.id}
                    size="sm"
                  >
                    Submit Response
                  </Button>
                </div>
              )}

              {checkIn.responses && checkIn.responses.length > 0 && (
                <div className="space-y-3 mt-4">
                  <p className="text-sm font-medium text-muted-foreground">
                    {checkIn.responses.length} {checkIn.responses.length === 1 ? 'response' : 'responses'}
                  </p>
                  {checkIn.responses.map((response) => (
                    <div key={response.id} className="flex gap-3 p-3 bg-muted rounded-lg">
                      <Avatar className="h-8 w-8 flex-shrink-0">
                        <AvatarImage src={response.user?.image} />
                        <AvatarFallback>
                          {response.user?.username?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">
                          {response.user?.name || response.user?.username}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {response.response_text}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
